import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Calendar,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Check,
  ShieldCheck,
  Eye,
  Edit,
  Send,
  Wallet,
  Landmark,
  AlertTriangle,
  FileCheck,
  Activity,
  RefreshCw,
  Brain,
  Sparkles,
  Search,
  Percent,
  Briefcase,
  CalendarClock,
  MessageSquare,
} from "lucide-react";
import { Modal, Input, InputNumber, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import LoanWhatsAppModal from "../components/LoanWhatsAppModal";
import { fintechApi } from "../services/api";

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const formatDateTime = (val) => {
  if (!val) return "-";
  const d = dayjs(val);
  return d.isValid() ? d.format("DD/MM/YYYY hh:mm A") : String(val);
};

const STATUS_CONFIG = {
  DRAFT: { label: "Draft", bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" },
  SUBMITTED: { label: "Submitted", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  UNDER_REVIEW: { label: "Under Review", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  APPROVED: { label: "Approved / Sanctioned", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  REJECTED: { label: "Rejected", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  DISBURSED: { label: "Disbursed", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  CLOSED: { label: "Closed", bg: "bg-slate-100", text: "text-slate-800", border: "border-slate-300" },
};

// CIBIL range 300–900 — meter ke bands (color bhi yahin se aata hai)
const CIBIL_MIN = 300;
const CIBIL_MAX = 900;
const CIBIL_BANDS = [
  { min: 300, max: 549, label: "POOR", color: "#ef4444", text: "text-red-700", badge: "bg-red-100 text-red-800" },
  { min: 550, max: 649, label: "FAIR", color: "#f97316", text: "text-orange-700", badge: "bg-orange-100 text-orange-800" },
  { min: 650, max: 699, label: "AVERAGE", color: "#eab308", text: "text-yellow-700", badge: "bg-yellow-100 text-yellow-800" },
  { min: 700, max: 749, label: "GOOD", color: "#3b82f6", text: "text-blue-700", badge: "bg-blue-100 text-blue-800" },
  { min: 750, max: 900, label: "EXCELLENT", color: "#10b981", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800" },
];

const getCibilRating = (score) => {
  if (score === null || score === undefined || score === "" || isNaN(Number(score))) return null;
  const s = Number(score);
  // Range ke bahar aaye to nearest band dikhate hain
  if (s < CIBIL_MIN) return CIBIL_BANDS[0];
  if (s > CIBIL_MAX) return CIBIL_BANDS[CIBIL_BANDS.length - 1];
  return CIBIL_BANDS.find((b) => s >= b.min && s <= b.max) || null;
};

// Meter pointer ki position (0–100%)
const cibilPercent = (score) => {
  const s = Number(score);
  if (isNaN(s)) return 0;
  return Math.min(100, Math.max(0, ((s - CIBIL_MIN) / (CIBIL_MAX - CIBIL_MIN)) * 100));
};

const toArray = (val) =>
  Array.isArray(val) ? val : Array.isArray(val?.content) ? val.content : Array.isArray(val?.data) ? val.data : null;

const normalizeDocument = (d) => ({
  ...d,
  id: d.id ?? d.documentId,
  name: d.name || d.documentType || d.fileName || "Document",
  uploadedAt: d.uploadedAt || d.createdAt,
  status: d.status || "PENDING",
  url: d.fileUrl || d.url || null,
});

// Audit action → circle color (spec)
const ACTION_COLORS = [
  { match: "CREATE", color: "#3b82f6" },
  { match: "SUBMIT", color: "#f97316" },
  { match: "REVIEW", color: "#eab308" },
  { match: "APPROVE", color: "#10b981" },
  { match: "SANCTION", color: "#10b981" },
  { match: "REJECT", color: "#ef4444" },
  { match: "DISBURSE", color: "#8b5cf6" },
  { match: "PAYMENT", color: "#10b981" },
];

const toTitle = (code) =>
  String(code || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalizeTimelineEvent = (e, idx) => {
  const action = String(e.action || e.type || e.status || "").toUpperCase();
  // Backend performedBy me employee id bhejta hai, naam performedByName me
  const performer = e.performedByName || e.user || e.performedBy || e.createdBy || e.actionBy;
  return {
    id: e.id ?? idx,
    action,
    title: e.title || e.description || toTitle(action) || "Update",
    date: e.date || e.createdAt || e.timestamp,
    user: typeof performer === "object" ? performer?.name : performer,
    role: e.performedByRole || e.role || e.designation || "",
    note: e.note || e.remarks || "",
    color: ACTION_COLORS.find((a) => action.includes(a.match))?.color || null,
  };
};

const normalizeLoan = (res, fallbackId) => ({
  ...res,
  id: res.id ?? fallbackId,
  applicationId: res.applicationId || res.applicationNumber || `#${res.id ?? fallbackId}`,
  applicantName: res.applicantName || "-",
  phone: res.contactNumber || res.phone || "",
  alternatePhone: res.alternateNumber || res.alternatePhone || "",
  dob: res.dateOfBirth || res.dob || "",
  amount: Number(res.loanAmount ?? res.amount) || 0,
  sanctionedAmount: Number(res.sanctionedAmount) || 0,
  tenure: Number(res.loanTenureMonths ?? res.tenure) || 0,
  interestRate: Number(res.interestRate) || 0,
  cibilScore: res.cibilScore ?? null,
  kycStatus: res.kycStatus || null,
  assignedTo:
    typeof res.assignedTo === "string"
      ? res.assignedTo
      : res.assignedToName || res.assignedTo?.name || "Unassigned",
  loanAccountId: res.loanAccountId ?? res.accountId ?? res.loanAccount?.id ?? null,
  documents: (toArray(res.documents) || []).map(normalizeDocument),
  timeline: (toArray(res.timeline) || toArray(res.statusHistory) || []).map(normalizeTimelineEvent),
});

// ── AI Credit Score helpers (0–100) ──
const AI_GAUGE_R = 70;
const AI_GAUGE_C = 2 * Math.PI * AI_GAUGE_R;

const getAiScoreColor = (score) => {
  const s = Number(score);
  if (isNaN(s)) return "#9ca3af";
  if (s >= 80) return "#10b981";
  if (s >= 60) return "#f97316";
  return "#ef4444";
};

const getAiScoreLabel = (score) => {
  const s = Number(score);
  if (isNaN(s)) return "-";
  if (s >= 80) return "LOW RISK";
  if (s >= 60) return "MEDIUM RISK";
  return "HIGH RISK";
};

const RECOMMENDATION_CONFIG = {
  AUTO_APPROVE: { label: "AUTO APPROVE", badge: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "#10b981" },
  MANUAL_REVIEW: { label: "MANUAL REVIEW", badge: "bg-orange-100 text-orange-800 border-orange-200", dot: "#f97316" },
  LIKELY_REJECT: { label: "LIKELY REJECT", badge: "bg-red-100 text-red-800 border-red-200", dot: "#ef4444" },
};

// Backend factor ke naam alag-alag ho sakte hain — jo mile bas wahi render hoga
const AI_FACTOR_META = [
  { label: "Income Ratio", icon: Percent, aliases: ["incomeratio", "income", "incomescore", "foir", "dti", "debttoincome"] },
  { label: "CIBIL Score", icon: ShieldCheck, aliases: ["cibil", "cibilscore", "bureau", "bureauscore", "creditscore"] },
  { label: "Employment", icon: Briefcase, aliases: ["employment", "employmentscore", "employmenttype", "job", "jobstability"] },
  { label: "Tenure", icon: CalendarClock, aliases: ["tenure", "tenurescore", "loantenure", "term"] },
];

const normalizeKey = (k) =>
  String(k || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

// Pehla valid number uthao, warna null (0 bhi valid value hai)
const pickNum = (...vals) => {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (!isNaN(n)) return n;
  }
  return null;
};

// Factors object { incomeRatio: {...} } ya array [{ name, score }] — dono shapes chalte hain
const normalizeAiFactors = (raw) => {
  if (!raw || typeof raw !== "object") return [];
  const entries = Array.isArray(raw)
    ? raw.map((f, i) => [f?.name || f?.factor || f?.key || f?.label || `factor${i}`, f])
    : Object.entries(raw);

  return entries
    .map(([rawName, val]) => {
      const nk = normalizeKey(rawName);
      const meta = AI_FACTOR_META.find((m) => m.aliases.includes(nk));
      const isObj = val && typeof val === "object";
      const score = isObj ? pickNum(val.score, val.value, val.points, val.rating) : pickNum(val);
      let weight = isObj ? pickNum(val.weight, val.weightage, val.weightPercent, val.weightPercentage) : null;
      // Weight 0.25 ki tarah fraction me aaye to percent bana lo
      if (weight !== null && weight > 0 && weight <= 1) weight = weight * 100;
      const max = isObj ? pickNum(val.maxScore, val.max, val.outOf) : null;
      if (score === null && weight === null) return null;
      return {
        key: nk || String(rawName),
        label: meta?.label || toTitle(rawName),
        Icon: meta?.icon || Activity,
        score,
        weight: weight === null ? null : Math.round(weight * 10) / 10,
        max,
        remark: (isObj && (val.remark || val.remarks || val.comment || val.description)) || "",
      };
    })
    .filter(Boolean);
};

// Response shape tolerant — res.data ya res dono handle
const normalizeAiScore = (res) => {
  const d = res?.data || res;
  if (!d || typeof d !== "object") return null;
  const score = pickNum(d.score, d.aiScore, d.creditScore, d.aiCreditScore, d.totalScore);
  const factors = normalizeAiFactors(d.factors || d.breakdown || d.scoreBreakdown || d.factorScores || d.components);
  const recommendation = String(d.recommendation || d.decision || d.suggestedAction || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (score === null && factors.length === 0 && !recommendation) return null;
  return {
    score,
    recommendation,
    maxAmount: pickNum(d.maxRecommendedAmount, d.maxLoanAmount, d.maxAmount, d.recommendedAmount),
    maxTenure: pickNum(d.maxRecommendedTenure, d.maxTenure, d.recommendedTenure, d.maxTenureMonths),
    factors,
    remarks: d.remarks || d.reason || d.summary || d.notes || "",
    generatedAt: d.generatedAt || d.scoredAt || d.createdAt || d.updatedAt || null,
  };
};

// CIBIL check response — jo field mile wahi dikhate hain
const normalizeCibilResult = (res) => {
  const d = res?.data || res;
  if (!d || typeof d !== "object") return null;
  const score = pickNum(d.cibilScore, d.score, d.bureauScore);
  const eligible =
    typeof d.eligible === "boolean" ? d.eligible : typeof d.isEligible === "boolean" ? d.isEligible : null;
  if (score === null && eligible === null) return null;
  return {
    score,
    rating: d.rating || d.grade || d.category || "",
    eligible,
    maxLoanAmount: pickNum(d.maxLoanAmount, d.maxEligibleAmount, d.eligibleAmount, d.maxAmount),
    checkedAt: d.checkedAt || d.verifiedAt || d.cibilCheckedAt || d.lastCheckedAt || d.updatedAt || null,
    remarks: d.remarks || d.message || "",
  };
};

export default function LoanApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentTheme } = useTheme();
  const { has } = usePermissions();
  const primaryColor = currentTheme?.color || "#2e856e";

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  // Sirf API data — pehle yahan fake defaults (CIBIL 750, SUBMITTED, aaj ki date) the
  const [loan, setLoan] = useState(null);

  // AI Credit Score (tab 6) — sirf API se aata hai, koi default score nahi
  const [aiScore, setAiScore] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // CIBIL bureau pull (tab 4)
  const [cibilResult, setCibilResult] = useState(null);
  const [cibilLoading, setCibilLoading] = useState(false);
  const [cibilError, setCibilError] = useState(null);

  // Modals
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [disburseModalOpen, setDisburseModalOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false); // 💬 status update WhatsApp
  const [docRejectModal, setDocRejectModal] = useState({ open: false, doc: null, remarks: "" });

  const [sanctionForm, setSanctionForm] = useState({
    sanctionedAmount: 0,
    interestRate: 0,
    tenure: 0,
  });
  const [rejectionReason, setRejectionReason] = useState("");
  const [disburseForm, setDisburseForm] = useState({
    utrNumber: "",
    accountNumber: "",
    disbursalDate: dayjs().format("YYYY-MM-DD"),
    disbursedAmount: 0,
  });

  const fetchLoanDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [loanRes, docsRes, auditRes] = await Promise.allSettled([
        fintechApi.getLoanById(id),
        fintechApi.getLoanDocuments(id),
        fintechApi.getAuditTrail(id),
      ]);

      if (loanRes.status === "rejected") throw loanRes.reason;
      const raw = loanRes.value?.data || loanRes.value;
      if (!raw) throw new Error("Loan application not found");

      const normalized = normalizeLoan(raw, id);
      if (docsRes.status === "fulfilled") {
        const docs = toArray(docsRes.value?.data ?? docsRes.value);
        if (docs) normalized.documents = docs.map(normalizeDocument);
      }
      // Audit trail API na bani ho to loan ke andar ki timeline hi dikhao
      if (auditRes.status === "fulfilled") {
        const audit = toArray(auditRes.value?.data ?? auditRes.value);
        if (audit) normalized.timeline = audit.map(normalizeTimelineEvent);
      } else {
        console.warn("Audit trail unavailable, using embedded timeline:", auditRes.reason?.message);
      }
      // Latest activity upar
      // (dayjs(undefined) = abhi ka time hota hai, isliye bina date wali entries neeche)
      const ts = (e) => (e.date && dayjs(e.date).isValid() ? dayjs(e.date).valueOf() : 0);
      normalized.timeline.sort((a, b) => ts(b) - ts(a));
      setLoan(normalized);

      const approvedAmount = normalized.sanctionedAmount || normalized.amount;
      setSanctionForm({
        sanctionedAmount: approvedAmount,
        interestRate: normalized.interestRate,
        tenure: normalized.tenure,
      });
      setDisburseForm({
        utrNumber: "",
        accountNumber: normalized.bankAccountNumber || "",
        disbursalDate: dayjs().format("YYYY-MM-DD"),
        disbursedAmount: approvedAmount,
      });
    } catch (err) {
      console.error("Error fetching loan detail:", err);
      setLoadError(err.message || "Failed to load loan application");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLoanDetail();
  }, [fetchLoanDetail]);

  // Doosri application khule to purana AI / CIBIL result saaf kar do
  useEffect(() => {
    setAiScore(null);
    setAiError(null);
    setCibilResult(null);
    setCibilError(null);
  }, [id]);

  // Dynamic EMI Calculation
  const emiCalculation = useMemo(() => {
    const P = Number(sanctionForm.sanctionedAmount) || Number(loan?.amount) || 0;
    const N = Number(sanctionForm.tenure) || Number(loan?.tenure) || 0;
    const annualRate = Number(sanctionForm.interestRate) || Number(loan?.interestRate) || 0;
    const R = annualRate / (12 * 100);

    if (P <= 0 || N <= 0 || R <= 0) return { emi: 0, totalPayable: 0, totalInterest: 0 };
    const emi = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
    const roundedEmi = Math.round(emi);
    const totalPayable = roundedEmi * N;
    return {
      emi: roundedEmi,
      totalPayable,
      totalInterest: totalPayable - P,
    };
  }, [sanctionForm, loan]);

  // API success pe hi server se fresh data — local fake timeline entries nahi banate
  const runAction = async (apiCall, successMsg, onSuccess) => {
    setActionLoading(true);
    try {
      const res = await apiCall();
      message.success(successMsg);
      if (onSuccess) onSuccess(res);
      await fetchLoanDetail();
      return true;
    } catch (err) {
      console.error("Loan action failed:", err);
      message.error(err.message || "Action failed. Please try again.");
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitDraft = () =>
    runAction(() => fintechApi.submitLoan(loan.id), "Application submitted for review");

  const handleStartReview = () =>
    runAction(() => fintechApi.reviewLoan(loan.id, "Underwriting check started"), "Loan moved to UNDER_REVIEW");

  const handleApprove = async () => {
    if (!(Number(sanctionForm.sanctionedAmount) > 0) || !(Number(sanctionForm.interestRate) > 0) || !(Number(sanctionForm.tenure) > 0)) {
      message.warning("Please enter sanctioned amount, interest rate and tenure");
      return;
    }
    const ok = await runAction(
      () => fintechApi.approveLoan(loan.id, sanctionForm),
      "Loan Sanctioned & Approved successfully!"
    );
    if (ok) setApproveModalOpen(false);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      message.warning("Please provide a rejection reason");
      return;
    }
    const ok = await runAction(
      () => fintechApi.rejectLoan(loan.id, rejectionReason.trim()),
      "Loan Application Rejected."
    );
    if (ok) {
      setRejectModalOpen(false);
      setRejectionReason("");
    }
  };

  const handleDisburse = async () => {
    // UTR pehle random generate hota tha — asli bank reference hi chahiye
    if (!disburseForm.utrNumber.trim()) {
      message.warning("Please enter the bank UTR / transaction reference");
      return;
    }
    if (!(Number(disburseForm.disbursedAmount) > 0)) {
      message.warning("Please enter a valid disbursal amount");
      return;
    }
    const sanctioned = loan.sanctionedAmount || loan.amount;
    if (sanctioned && Number(disburseForm.disbursedAmount) > sanctioned) {
      message.warning(`Disbursal amount cannot exceed sanctioned amount ${formatINR(sanctioned)}`);
      return;
    }

    let accountId = null;
    const ok = await runAction(
      () => fintechApi.disburseLoan(loan.id, { ...disburseForm, utrNumber: disburseForm.utrNumber.trim().toUpperCase() }),
      "Funds Disbursed & Loan Account Created!",
      (res) => {
        const acc = res?.data || res;
        accountId = acc?.id ?? acc?.loanAccountId ?? acc?.accountId ?? null;
      }
    );
    if (!ok) return;
    setDisburseModalOpen(false);
    // Pehle application id ko hi account id maan ke navigate hota tha
    if (accountId) navigate(`/fintech/loan-accounts/${accountId}`);
  };

  const handleVerifyDocument = (doc) =>
    runAction(() => fintechApi.verifyDocument(loan.id, doc.id), `${doc.name} marked as Verified`);

  const handleRejectDocument = async () => {
    const { doc, remarks } = docRejectModal;
    if (!remarks.trim()) {
      message.warning("Please provide a reason for rejecting the document");
      return;
    }
    const ok = await runAction(
      () => fintechApi.rejectDocument(loan.id, doc.id, remarks.trim()),
      `${doc.name} marked as Rejected`
    );
    if (ok) setDocRejectModal({ open: false, doc: null, remarks: "" });
  };

  // PAN loan data se hi lete hain — form me na ho to CIBIL check disabled rahega
  const panNumber = String(loan?.panNumber || loan?.pan || "").trim();

  // AI engine fail ho to score set nahi karte aur success message bhi nahi dikhate
  const handleRunAiScore = async () => {
    if (!loan?.id || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await fintechApi.getAiCreditScore(loan.id);
      const parsed = normalizeAiScore(res);
      if (!parsed) throw new Error("AI engine did not return a credit score for this application");
      setAiScore(parsed);
      message.success("AI credit score generated");
    } catch (err) {
      console.error("AI credit score failed:", err);
      setAiScore(null);
      setAiError(err.message || "Failed to generate AI credit score");
      message.error(err.message || "Failed to generate AI credit score");
    } finally {
      setAiLoading(false);
    }
  };

  // CIBIL pull — bureau se score aane par hi success + loan detail refresh
  const handleCheckCibil = async () => {
    if (!loan?.id || cibilLoading) return;
    if (!panNumber) {
      message.warning("Applicant PAN number is missing, cannot run the bureau check");
      return;
    }
    setCibilLoading(true);
    setCibilError(null);
    try {
      const res = await fintechApi.checkCibil(loan.id, panNumber);
      const parsed = normalizeCibilResult(res);
      if (!parsed) throw new Error("Bureau did not return a CIBIL score");
      setCibilResult(parsed);
      message.success("CIBIL report pulled successfully");
      fetchLoanDetail();
    } catch (err) {
      console.error("CIBIL check failed:", err);
      setCibilResult(null);
      setCibilError(err.message || "Failed to fetch CIBIL score");
      message.error(err.message || "Failed to fetch CIBIL score");
    } finally {
      setCibilLoading(false);
    }
  };

  if (loading && !loan) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor, borderTopColor: "transparent" }} />
      </div>
    );
  }

  if (!loan) {
    return (
      <div className="bg-white p-10 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col items-center text-center gap-3">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <h2 className="text-lg font-bold text-gray-900">Could not load loan application</h2>
        <p className="text-sm text-gray-500 max-w-md">{loadError}</p>
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => navigate("/fintech/loans")}
            className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Back to Applications
          </button>
          <button
            onClick={fetchLoanDetail}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-2"
            style={{ backgroundColor: primaryColor }}
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_CONFIG[loan.status] || STATUS_CONFIG.DRAFT;
  // Fresh bureau pull ho to wahi dikhao, warna loan record wala score
  const displayCibilScore =
    cibilResult?.score ?? (loan.cibilScore === null || loan.cibilScore === undefined ? null : Number(loan.cibilScore));
  const cibilRating = getCibilRating(displayCibilScore);
  const cibilCheckedAt = cibilResult?.checkedAt || loan.cibilCheckedAt || loan.cibilVerifiedAt || null;
  const aiRecommendation = aiScore?.recommendation ? RECOMMENDATION_CONFIG[aiScore.recommendation] : null;
  const canDecide = has("LOAN_APPROVE");

  return (
    <div className="w-full space-y-6 pb-16">
      {/* ── Top Header Bar ── */}
      <div className="bg-white p-6 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate("/fintech/loans")}
            className="p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition-colors mt-1"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-xl md:text-2xl font-black text-gray-900">
                {loan.applicantName}
              </h1>
              <span className="font-mono text-xs font-bold px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg">
                {loan.applicationId}
              </span>
              <span
                className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-bold border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
              >
                {statusInfo.label}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mt-2">
              {loan.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5" />
                  {loan.phone}
                </span>
              )}
              {loan.email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  {loan.email}
                </span>
              )}
              {loan.createdAt && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Created: {dayjs(loan.createdAt).format("DD/MM/YYYY")}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Workflow Actions */}
        <div className="flex flex-wrap items-center gap-2">
          {["DRAFT", "SUBMITTED"].includes(loan.status) && has("LOAN_EDIT") && (
            <button
              onClick={() => navigate(`/fintech/loans/${loan.id}/edit`)}
              className="px-4 py-2.5 bg-white hover:bg-gray-50 text-gray-700 text-xs font-bold rounded-xl border border-gray-300 transition-all flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}

          {/* 💬 WhatsApp — applicant ko status ka message manually bhejo */}
          <button
            onClick={() => setWaOpen(true)}
            title="Send loan status update on WhatsApp"
            className="px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-xl border border-emerald-200 transition-all flex items-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            <span>WhatsApp</span>
          </button>

          {loan.status === "DRAFT" && has("LOAN_EDIT") && (
            <button
              onClick={handleSubmitDraft}
              disabled={actionLoading}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              <span>Submit for Review</span>
            </button>
          )}

          {loan.status === "SUBMITTED" && canDecide && (
            <button
              onClick={handleStartReview}
              disabled={actionLoading}
              className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-60"
            >
              <Clock className="w-4 h-4" />
              <span>Start Review</span>
            </button>
          )}

          {loan.status === "UNDER_REVIEW" && canDecide && (
            <>
              <button
                onClick={() => setApproveModalOpen(true)}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Sanction / Approve</span>
              </button>
              <button
                onClick={() => setRejectModalOpen(true)}
                className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-xl border border-red-200 transition-all flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                <span>Reject</span>
              </button>
            </>
          )}

          {loan.status === "APPROVED" && has("LOAN_DISBURSE") && (
            <button
              onClick={() => setDisburseModalOpen(true)}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-lg transition-all flex items-center gap-2"
            >
              <Landmark className="w-4 h-4" />
              <span>Disburse Loan Funds</span>
            </button>
          )}

          {loan.status === "DISBURSED" && loan.loanAccountId && has("LOAN_ACCOUNT_VIEW") && (
            <button
              onClick={() => navigate(`/fintech/loan-accounts/${loan.loanAccountId}`)}
              className="px-5 py-2.5 text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              <Wallet className="w-4 h-4" />
              <span>View Active Loan Account</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Tabbed View ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden p-6">
        <div className="flex border-b border-gray-200 gap-6 overflow-x-auto pb-2 text-sm font-bold">
          {[
            { id: "1", label: "Applicant Profile", icon: User },
            { id: "2", label: "Loan & EMI Terms", icon: Wallet },
            { id: "3", label: `Documents Hub (${loan.documents.length})`, icon: FileCheck },
            { id: "4", label: "KYC & Bureau Score", icon: ShieldCheck },
            { id: "5", label: "Activity Log", icon: Activity },
            { id: "6", label: "AI Credit Score", icon: Brain },
          ].map((tab) => {
            const Icon = tab.icon;
            const isSel = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-2 transition-all whitespace-nowrap ${
                  isSel ? "border-b-2" : "text-gray-400 hover:text-gray-700"
                }`}
                style={isSel ? { color: primaryColor, borderColor: primaryColor } : {}}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {/* ── Tab 1: Applicant Profile ── */}
          {activeTab === "1" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="space-y-4">
                <h3 className="font-bold text-gray-900 border-b pb-2 text-xs uppercase tracking-wider text-gray-500">
                  Personal Details
                </h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">Full Name</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{loan.applicantName}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Date of Birth / Gender</span>
                    <div className="font-semibold text-gray-900 mt-0.5">
                      {loan.dob ? dayjs(loan.dob).format("DD/MM/YYYY") : "-"} ({loan.gender || "-"})
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">PAN Number</span>
                    <div className="font-mono font-bold text-gray-900 mt-0.5">{loan.panNumber || "-"}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Aadhaar Number</span>
                    <div className="font-mono font-bold text-gray-900 mt-0.5">{loan.aadhaarNumber || "-"}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Alternate Phone</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{loan.alternatePhone || "-"}</div>
                  </div>
                </div>

                <h3 className="font-bold text-gray-900 border-b pb-2 pt-2 text-xs uppercase tracking-wider text-gray-500">
                  Residential Address
                </h3>
                <p className="text-xs text-gray-700 leading-relaxed bg-gray-50 p-3 rounded-xl">
                  {[loan.address, loan.city, loan.state, loan.pincode].filter(Boolean).join(", ") || "-"}
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="font-bold text-gray-900 border-b pb-2 text-xs uppercase tracking-wider text-gray-500">
                  Employment & Banking
                </h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">Employment Type</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{loan.employmentType || "-"}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Monthly Net Income</span>
                    <div className="font-bold text-emerald-700 mt-0.5">{formatINR(loan.monthlyIncome)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Company Name</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{loan.companyName || "-"}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Disbursal Account</span>
                    <div className="font-mono font-semibold text-gray-900 mt-0.5">{loan.bankAccountNumber || "-"}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Bank IFSC</span>
                    <div className="font-mono font-semibold text-gray-900 mt-0.5">{loan.ifscCode || "-"}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Assigned Underwriter</span>
                    <div className="font-semibold text-gray-900 mt-0.5">{loan.assignedTo}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab 2: Loan & EMI Terms ── */}
          {activeTab === "2" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <span className="text-xs text-gray-500">Requested Amount</span>
                  <div className="text-xl font-black text-gray-900 mt-1">{formatINR(loan.amount)}</div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                  <span className="text-xs text-emerald-800 font-bold">Sanctioned Amount</span>
                  <div className="text-xl font-black text-emerald-900 mt-1">
                    {loan.sanctionedAmount ? formatINR(loan.sanctionedAmount) : "Not sanctioned"}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <span className="text-xs text-gray-500">Interest Rate</span>
                  <div className="text-xl font-black text-gray-900 mt-1">{loan.interestRate}% p.a.</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                  <span className="text-xs text-gray-500">Monthly EMI</span>
                  <div className="text-xl font-black mt-1" style={{ color: primaryColor }}>{formatINR(emiCalculation.emi)}</div>
                </div>
              </div>

              {/* Repayment Schedule Preview Table */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="p-4 bg-gray-50 font-bold text-xs text-gray-700 uppercase tracking-wider">
                  Projected Repayment Summary ({loan.tenure} Months Amortization)
                </div>
                <div className="p-4 text-xs space-y-2">
                  <div className="flex justify-between py-1 border-b">
                    <span className="text-gray-500">Principal Component:</span>
                    <strong className="text-gray-900">{formatINR(loan.sanctionedAmount || loan.amount)}</strong>
                  </div>
                  <div className="flex justify-between py-1 border-b">
                    <span className="text-gray-500">Total Interest over tenure:</span>
                    <strong className="text-emerald-700">{formatINR(emiCalculation.totalInterest)}</strong>
                  </div>
                  <div className="flex justify-between py-1 font-bold">
                    <span className="text-gray-800">Total Repayment Amount:</span>
                    <strong className="text-gray-900">{formatINR(emiCalculation.totalPayable)}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab 3: Documents Hub ── */}
          {activeTab === "3" && (
            <div className="space-y-4">
              {loan.documents.length === 0 ? (
                <div className="py-10 text-center text-gray-400">
                  <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="font-semibold">No documents uploaded yet</p>
                  {["DRAFT", "SUBMITTED"].includes(loan.status) && has("LOAN_EDIT") && (
                    <p className="text-xs mt-1">Use Edit to attach KYC documents.</p>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {loan.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="p-4 bg-gray-50 rounded-2xl border border-gray-200 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-bold text-sm text-gray-900 flex items-center gap-2">
                          <FileText className="w-4 h-4" style={{ color: primaryColor }} />
                          <span>{doc.name}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">Uploaded: {formatDateTime(doc.uploadedAt)}</div>
                        <span
                          className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                            doc.status === "VERIFIED"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : doc.status === "REJECTED"
                              ? "bg-red-50 text-red-700 border-red-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                          }`}
                        >
                          {doc.status}
                        </span>
                        {doc.status === "REJECTED" && doc.remarks && (
                          <div className="text-[11px] text-red-600 mt-1">{doc.remarks}</div>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5">
                        {doc.url && (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                            title="View Document"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                        )}
                        {canDecide && doc.status !== "VERIFIED" && (
                          <button
                            onClick={() => handleVerifyDocument(doc)}
                            disabled={actionLoading}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all disabled:opacity-50"
                            title="Mark as Verified"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {canDecide && doc.status !== "REJECTED" && (
                          <button
                            onClick={() => setDocRejectModal({ open: true, doc, remarks: "" })}
                            disabled={actionLoading}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-xl transition-all disabled:opacity-50"
                            title="Reject Document"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Tab 4: KYC & Bureau Score — sirf backend data, koi hardcoded "VALIDATED" nahi ── */}
          {activeTab === "4" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 bg-gradient-to-br from-white to-emerald-50/40 rounded-2xl border border-emerald-100 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-xs font-bold uppercase text-gray-400">Credit Score (CIBIL)</span>
                    {cibilResult && cibilResult.eligible !== null && (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-bold ${
                          cibilResult.eligible ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"
                        }`}
                      >
                        {cibilResult.eligible ? "ELIGIBLE" : "NOT ELIGIBLE"}
                      </span>
                    )}
                  </div>

                  {cibilRating ? (
                    <div className={`text-4xl font-black mt-2 ${cibilRating.text}`}>
                      {displayCibilScore}{" "}
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${cibilRating.badge}`}>
                        {cibilResult?.rating || cibilRating.label}
                      </span>
                    </div>
                  ) : (
                    <div className="text-2xl font-black text-gray-400 mt-2">Not checked</div>
                  )}

                  {/* Score meter 300–900 — band wise colors */}
                  <div className="mt-5">
                    <div className="flex h-2.5 rounded-full overflow-hidden">
                      {CIBIL_BANDS.map((band) => (
                        <div
                          key={band.label}
                          title={`${band.min}-${band.max} ${band.label}`}
                          style={{
                            backgroundColor: band.color,
                            width: `${((band.max - band.min + 1) / (CIBIL_MAX - CIBIL_MIN + 1)) * 100}%`,
                            opacity: cibilRating && cibilRating.label !== band.label ? 0.3 : 1,
                          }}
                        />
                      ))}
                    </div>

                    {/* Pointer sirf tab jab score aa chuka ho */}
                    <div className="relative h-4">
                      {cibilRating && (
                        <span
                          className="absolute -top-1 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2 border-white shadow-md"
                          style={{
                            left: `${cibilPercent(displayCibilScore)}%`,
                            backgroundColor: cibilRating.color,
                            transition: "left 1s ease",
                          }}
                        />
                      )}
                    </div>

                    <div className="flex justify-between text-[10px] font-bold text-gray-400">
                      <span>300</span>
                      <span>550</span>
                      <span>650</span>
                      <span>700</span>
                      <span>750</span>
                      <span>900</span>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {CIBIL_BANDS.map((band) => (
                        <span
                          key={band.label}
                          className={`px-2 py-1 rounded-full text-[10px] font-bold border ${
                            cibilRating?.label === band.label ? "" : "opacity-50"
                          }`}
                          style={{
                            color: band.color,
                            borderColor: `${band.color}55`,
                            backgroundColor: `${band.color}14`,
                          }}
                        >
                          {band.label} {band.min}-{band.max}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Bureau response ke extra fields — jo mile bas wahi */}
                  {cibilResult && (cibilResult.maxLoanAmount !== null || cibilResult.remarks) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-xs">
                      {cibilResult.maxLoanAmount !== null && (
                        <div className="p-3 bg-white rounded-xl border border-gray-200">
                          <span className="text-gray-500">Max Eligible Loan Amount</span>
                          <div className="font-black text-gray-900 mt-0.5">{formatINR(cibilResult.maxLoanAmount)}</div>
                        </div>
                      )}
                      {cibilResult.remarks && (
                        <div className="p-3 bg-white rounded-xl border border-gray-200 text-gray-600">
                          {cibilResult.remarks}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-5">
                  {cibilError && (
                    <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{cibilError}</span>
                    </div>
                  )}

                  <div className="text-xs text-gray-500 mb-3">
                    {cibilRating
                      ? cibilCheckedAt
                        ? `Bureau report pulled on ${formatDateTime(cibilCheckedAt)}`
                        : "Bureau score received from backend."
                      : "Bureau check has not been run for this application yet."}
                  </div>

                  <button
                    onClick={handleCheckCibil}
                    disabled={cibilLoading || !panNumber}
                    className="px-5 py-2.5 bg-[#2e856e] text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: primaryColor }}
                    title={panNumber ? `Run bureau check for PAN ${panNumber}` : "Applicant PAN number is missing"}
                  >
                    {cibilLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    <span>
                      {cibilLoading
                        ? "Fetching Bureau Report..."
                        : cibilRating
                        ? "Re-check CIBIL Score"
                        : "Check CIBIL Score"}
                    </span>
                  </button>

                  <div className="text-[11px] mt-2">
                    {panNumber ? (
                      <span className="text-gray-400">
                        Using applicant PAN <strong className="font-mono text-gray-600">{panNumber}</strong>
                      </span>
                    ) : (
                      <span className="text-amber-600 font-semibold">
                        PAN number is missing on this application, so the bureau check cannot run. Add the PAN via Edit.
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200 space-y-3 text-xs">
                <div className="font-bold text-gray-900 border-b pb-2 flex justify-between">
                  <span>eKYC & Identity Authentication</span>
                  <span className="text-gray-500">{loan.kycStatus || "PENDING"}</span>
                </div>
                {[
                  { label: "PAN Verification", value: loan.panVerified },
                  { label: "Aadhaar eKYC", value: loan.aadhaarVerified },
                  { label: "Bank Account Verification", value: loan.bankVerified },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between">
                    <span>{item.label}:</span>
                    {item.value === true ? (
                      <strong className="text-emerald-700">VERIFIED</strong>
                    ) : item.value === false ? (
                      <strong className="text-red-700">FAILED</strong>
                    ) : (
                      <strong className="text-amber-700">PENDING</strong>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Tab 5: Activity Log (audit trail) ── */}
          {activeTab === "5" && (
            <div>
              {loan.timeline.length === 0 ? (
                <div className="py-10 text-center text-gray-400">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="font-semibold">No activity recorded yet</p>
                </div>
              ) : (
                <ol className="relative">
                  {loan.timeline.map((event, idx) => {
                    const dotColor = event.color || primaryColor;
                    const isLast = idx === loan.timeline.length - 1;
                    const d = dayjs(event.date);
                    return (
                      <li key={event.id} className="flex gap-4 text-xs">
                        {/* Vertical line + colored circle */}
                        <div className="flex flex-col items-center">
                          <span
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ring-4"
                            style={{ backgroundColor: `${dotColor}22`, "--tw-ring-color": `${dotColor}11` }}
                          >
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: dotColor }} />
                          </span>
                          {!isLast && <span className="w-px flex-1 bg-gray-200 my-1" />}
                        </div>
                        <div className={`flex-1 ${isLast ? "" : "pb-5"}`}>
                          <div className="flex flex-wrap justify-between gap-2">
                            <span className="font-bold text-sm text-gray-900">{event.title}</span>
                            <span className="text-gray-400">
                              {d.isValid() ? d.format("DD MMM YYYY, h:mm A") : event.date || "-"}
                            </span>
                          </div>
                          {event.user && (
                            <div className="text-gray-500 mt-0.5">
                              By: <span className="font-semibold text-gray-700">{event.user}</span>
                              {event.role && <> ({toTitle(event.role)})</>}
                            </div>
                          )}
                          {event.note && (
                            <div className="mt-1.5 p-2 bg-gray-50 rounded-lg border border-gray-200 text-gray-600">
                              {event.note}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {/* ── Tab 6: AI Credit Score — sirf engine ke response se render hota hai ── */}
          {activeTab === "6" && (
            <div className="space-y-6">
              {!aiScore ? (
                /* Score aane se pehle explanatory empty state + Run button */
                <div className="py-10 flex flex-col items-center text-center gap-3">
                  {aiError ? (
                    <AlertTriangle className="w-10 h-10 text-amber-500" />
                  ) : (
                    <Brain className="w-10 h-10 opacity-30" style={{ color: primaryColor }} />
                  )}
                  <h3 className="font-bold text-gray-900">
                    {aiError ? "Could not generate the AI credit score" : "No AI credit score generated yet"}
                  </h3>
                  <p className="text-xs text-gray-500 max-w-md">
                    {aiError ||
                      "The AI engine scores this file out of 100 using income ratio, bureau score, employment profile and requested tenure, and suggests a sanction decision along with the maximum amount and tenure it considers safe."}
                  </p>
                  <button
                    onClick={handleRunAiScore}
                    disabled={aiLoading}
                    className="mt-1 px-5 py-2.5 bg-[#2e856e] text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {aiLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : aiError ? (
                      <RefreshCw className="w-4 h-4" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    <span>{aiLoading ? "Scoring..." : aiError ? "Retry" : "Run AI Score"}</span>
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Circular gauge 0–100 */}
                    <div className="lg:col-span-5 p-6 bg-white rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col items-center">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                        AI Credit Score
                      </span>

                      <div className="relative w-[180px] h-[180px] mt-4">
                        <svg width="180" height="180" viewBox="0 0 180 180" className="-rotate-90">
                          <circle cx="90" cy="90" r={AI_GAUGE_R} fill="none" stroke="#f3f4f6" strokeWidth="16" />
                          {aiScore.score !== null && (
                            <circle
                              cx="90"
                              cy="90"
                              r={AI_GAUGE_R}
                              fill="none"
                              stroke={getAiScoreColor(aiScore.score)}
                              strokeWidth="16"
                              strokeLinecap="round"
                              strokeDasharray={AI_GAUGE_C}
                              strokeDashoffset={
                                AI_GAUGE_C - (AI_GAUGE_C * Math.min(100, Math.max(0, aiScore.score))) / 100
                              }
                              style={{ transition: "stroke-dashoffset 1s ease" }}
                            />
                          )}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span
                            className="text-5xl font-black"
                            style={{ color: aiScore.score !== null ? getAiScoreColor(aiScore.score) : "#9ca3af" }}
                          >
                            {aiScore.score !== null ? Math.round(aiScore.score) : "-"}
                          </span>
                          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
                            {aiScore.score !== null ? "out of 100" : "score not returned"}
                          </span>
                        </div>
                      </div>

                      {aiScore.score !== null && (
                        <span
                          className="mt-4 px-2 py-1 rounded-full text-xs font-bold"
                          style={{
                            color: getAiScoreColor(aiScore.score),
                            backgroundColor: `${getAiScoreColor(aiScore.score)}1a`,
                          }}
                        >
                          {getAiScoreLabel(aiScore.score)}
                        </span>
                      )}

                      {aiScore.generatedAt && (
                        <div className="text-[11px] text-gray-400 mt-3">
                          Generated on {formatDateTime(aiScore.generatedAt)}
                        </div>
                      )}

                      <button
                        onClick={handleRunAiScore}
                        disabled={aiLoading}
                        className="mt-4 px-5 py-2.5 bg-[#2e856e] text-white text-xs font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-60"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {aiLoading ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                        <span>{aiLoading ? "Scoring..." : "Re-run AI Score"}</span>
                      </button>
                    </div>

                    {/* Recommendation + max limits */}
                    <div className="lg:col-span-7 space-y-4">
                      <div className="p-6 bg-white rounded-2xl border border-[#e5e7eb] shadow-sm">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                          AI Recommendation
                        </span>
                        <div className="mt-3">
                          {aiScore.recommendation ? (
                            <span
                              className={`inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-bold border ${
                                aiRecommendation?.badge || "bg-gray-100 text-gray-700 border-gray-200"
                              }`}
                            >
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: aiRecommendation?.dot || "#6b7280" }}
                              />
                              {aiRecommendation?.label || toTitle(aiScore.recommendation).toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 font-semibold">
                              No recommendation returned by the AI engine
                            </span>
                          )}
                        </div>
                        {aiScore.remarks && (
                          <p className="text-xs text-gray-600 mt-3 leading-relaxed bg-gray-50 p-3 rounded-xl">
                            {aiScore.remarks}
                          </p>
                        )}
                      </div>

                      {aiScore.maxAmount !== null || aiScore.maxTenure !== null ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {aiScore.maxAmount !== null && (
                            <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                              <span className="text-xs text-emerald-800 font-bold">Max Recommended Amount</span>
                              <div className="text-xl font-black text-emerald-900 mt-1">
                                {formatINR(aiScore.maxAmount)}
                              </div>
                            </div>
                          )}
                          {aiScore.maxTenure !== null && (
                            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200">
                              <span className="text-xs text-gray-500">Max Recommended Tenure</span>
                              <div className="text-xl font-black text-gray-900 mt-1">
                                {aiScore.maxTenure} Months
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 rounded-2xl border border-dashed border-gray-300 text-xs text-gray-500">
                          The AI engine did not return a maximum recommended amount or tenure for this application.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Factor wise breakdown — jo factor API ne diye sirf wahi */}
                  <div>
                    <h3 className="font-bold text-gray-900 border-b pb-2 text-xs uppercase tracking-wider text-gray-500 mb-4">
                      Score Breakdown
                    </h3>
                    {aiScore.factors.length === 0 ? (
                      <div className="p-4 rounded-2xl border border-dashed border-gray-300 text-xs text-gray-500 text-center">
                        The AI engine did not return a factor-wise breakdown for this application.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {aiScore.factors.map((factor) => {
                          const Icon = factor.Icon;
                          const barPct =
                            factor.score === null
                              ? null
                              : factor.max
                              ? Math.min(100, Math.max(0, (factor.score / factor.max) * 100))
                              : Math.min(100, Math.max(0, factor.score));
                          return (
                            <div
                              key={factor.key}
                              className="p-4 bg-white rounded-2xl border border-[#e5e7eb] shadow-sm"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-xs font-bold text-gray-700">
                                  <Icon className="w-4 h-4" style={{ color: primaryColor }} />
                                  <span>{factor.label}</span>
                                </div>
                                {factor.weight !== null && (
                                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-600">
                                    {factor.weight}%
                                  </span>
                                )}
                              </div>

                              <div className="mt-3 text-2xl font-black text-gray-900">
                                {factor.score !== null ? factor.score : "-"}
                                {factor.score !== null && factor.max ? (
                                  <span className="text-xs font-bold text-gray-400"> / {factor.max}</span>
                                ) : null}
                              </div>

                              {barPct !== null && (
                                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${barPct}%`,
                                      backgroundColor: getAiScoreColor(barPct),
                                      transition: "width 1s ease",
                                    }}
                                  />
                                </div>
                              )}

                              <div className="text-[11px] text-gray-400 mt-2">
                                {factor.weight !== null
                                  ? `Weight ${factor.weight}% of total score`
                                  : "Weight not provided"}
                                {factor.remark ? ` • ${factor.remark}` : ""}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Approve Modal ── */}
      <Modal
        title={<div className="font-bold text-emerald-700">Sanction & Approve Loan</div>}
        open={approveModalOpen}
        onOk={handleApprove}
        onCancel={() => setApproveModalOpen(false)}
        okText="Confirm Sanction"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#10b981", borderColor: "#10b981" } }}
      >
        <div className="space-y-4 py-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Sanctioned Amount (₹)</label>
            <InputNumber
              className="w-full rounded-xl"
              min={0}
              value={sanctionForm.sanctionedAmount}
              onChange={(val) => setSanctionForm((prev) => ({ ...prev, sanctionedAmount: val }))}
              formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Interest Rate (% p.a.)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={0}
                max={60}
                step={0.25}
                value={sanctionForm.interestRate}
                onChange={(val) => setSanctionForm((prev) => ({ ...prev, interestRate: val }))}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Tenure (Months)</label>
              <InputNumber
                className="w-full rounded-xl"
                min={1}
                max={360}
                value={sanctionForm.tenure}
                onChange={(val) => setSanctionForm((prev) => ({ ...prev, tenure: val }))}
              />
            </div>
          </div>
          <div className="text-xs text-gray-500">
            EMI at these terms: <strong className="text-gray-900">{formatINR(emiCalculation.emi)}</strong> / month
          </div>
        </div>
      </Modal>

      {/* ── Reject Modal ── */}
      <Modal
        title={<div className="font-bold text-red-600">Reject Application</div>}
        open={rejectModalOpen}
        onOk={handleReject}
        onCancel={() => setRejectModalOpen(false)}
        okText="Confirm Rejection"
        confirmLoading={actionLoading}
        okButtonProps={{ danger: true }}
      >
        <div className="py-2 text-sm space-y-2">
          <label className="block text-xs font-bold text-gray-700">Rejection Reason *</label>
          <Input.TextArea
            rows={3}
            placeholder="Explain why this file was declined..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="rounded-xl"
          />
        </div>
      </Modal>

      {/* ── Document Reject Modal ── */}
      <Modal
        title={<div className="font-bold text-red-600">Reject Document</div>}
        open={docRejectModal.open}
        onOk={handleRejectDocument}
        onCancel={() => setDocRejectModal({ open: false, doc: null, remarks: "" })}
        okText="Reject Document"
        confirmLoading={actionLoading}
        okButtonProps={{ danger: true }}
      >
        <div className="py-2 text-sm space-y-2">
          <p className="text-xs text-gray-600">
            Rejecting <strong>{docRejectModal.doc?.name}</strong>
          </p>
          <label className="block text-xs font-bold text-gray-700">Reason *</label>
          <Input.TextArea
            rows={3}
            placeholder="e.g. Image blurred, name mismatch with PAN..."
            value={docRejectModal.remarks}
            onChange={(e) => setDocRejectModal((prev) => ({ ...prev, remarks: e.target.value }))}
            className="rounded-xl"
          />
        </div>
      </Modal>

      {/* ── Disburse Modal ── */}
      <Modal
        title={<div className="font-bold text-purple-700">Disburse Loan Funds to Borrower</div>}
        open={disburseModalOpen}
        onOk={handleDisburse}
        onCancel={() => setDisburseModalOpen(false)}
        okText="Transfer & Activate Account"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#7c3aed", borderColor: "#7c3aed" } }}
      >
        <div className="space-y-4 py-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Disbursal Amount (₹)</label>
            <InputNumber
              className="w-full rounded-xl font-bold"
              min={0}
              value={disburseForm.disbursedAmount}
              onChange={(val) => setDisburseForm((prev) => ({ ...prev, disbursedAmount: val }))}
              formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Bank UTR / Transaction Reference ID *</label>
            <Input
              placeholder="Enter UTR from the bank transfer"
              value={disburseForm.utrNumber}
              onChange={(e) => setDisburseForm((prev) => ({ ...prev, utrNumber: e.target.value }))}
              className="rounded-xl font-mono uppercase"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Target Account Number</label>
            <Input value={disburseForm.accountNumber || "Not provided"} disabled className="rounded-xl font-mono" />
          </div>
        </div>
      </Modal>

      {/* 💬 Loan status WhatsApp — template status ke hisaab se prefill hota hai */}
      <LoanWhatsAppModal
        open={waOpen}
        onClose={() => setWaOpen(false)}
        phone={loan.phone}
        templateKeys={["APPLICATION_SUBMITTED", "LOAN_APPROVED", "LOAN_REJECTED"]}
        defaultTemplate={
          loan.status === "REJECTED"
            ? "LOAN_REJECTED"
            : ["APPROVED", "DISBURSED"].includes(loan.status)
              ? "LOAN_APPROVED"
              : "APPLICATION_SUBMITTED"
        }
        context={{
          name: loan.applicantName,
          applicationId: loan.applicationId,
          amount: loan.sanctionedAmount || loan.amount,
        }}
      />
    </div>
  );
}
